import { ChevronRight, Eye, GitMerge, LogOut, Settings as SettingsIcon, Store } from "lucide-react";

import { TUNABLES } from "../../shared/tunables";
import { session } from "../auth";
import { useTunables } from "../lib/tunables";
import { ThemeToggle } from "../shell/ThemeToggle";
import { Button, HowItWorks, Page, Point } from "../ui";

const SECTIONS = [
	{
		href: "#/manage/stores",
		label: "Stores",
		Icon: Store,
		hint: "Which shops are read, how often, and which of their categories count.",
	},
	{
		href: "#/manage/watchlist",
		label: "Watchlist",
		Icon: Eye,
		hint: "Products checked every few minutes, and rules for what sends an alert.",
	},
	{
		href: "#/manage/grouping",
		label: "Grouping review",
		Icon: GitMerge,
		hint: "Match the same product across stores, or split wrong matches.",
	},
	{
		href: "#/manage/settings",
		label: "Settings",
		Icon: SettingsIcon,
		hint: "Discord alerts, AI matching, in-store alerts and checkout details.",
	},
];

export function Manage() {
	const { get } = useTunables();
	return (
		<Page
			title="Manage"
			help={
				<HowItWorks title="How scalpmania works">
					<Point lead="Sweeps">
						read every store's catalogue on a timer, every {get(TUNABLES.sweepEvery)} minutes unless you change it, and
						note new products, restocks and price changes.
					</Point>
					<Point lead="Watches">
						check the products you care about far more often, as often as every {get(TUNABLES.watchHot)} seconds,
						including stock in the physical shops you pick.
					</Point>
					<Point lead="Alerts">
						go to Discord for watched products and anything matching a rule. Everything found also shows up on Now.
					</Point>
					<Point lead="Grouping">
						puts the same product from different stores together, mostly by barcode, so you can see who has it cheapest.
						AI can match the rest, and merge them for you if you let it.
					</Point>
					<Point lead="Buy now">
						fills in the cart and checkout at Pokechest and Ringo for you, then waits for you to approve the payment in
						Vipps or Klarna.
					</Point>
				</HowItWorks>
			}
			subtitle="Everything you set once and rarely revisit."
			width="wide"
		>
			<ul className="mb-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
				{SECTIONS.map(({ href, label, Icon, hint }) => (
					<li key={href} className="grid">
						<a
							href={href}
							className="group flex flex-col gap-2 rounded-[10px] border border-hairline bg-paper p-3 hover:border-line hover:bg-raised"
						>
							<span className="flex items-center gap-2">
								<span className="flex size-7 items-center justify-center rounded-lg bg-raised text-ink group-hover:bg-paper">
									<Icon className="size-3.5" aria-hidden />
								</span>
								<span className="text-[13px] font-semibold">{label}</span>
								<ChevronRight className="ml-auto size-4 text-muted group-hover:text-ink" aria-hidden />
							</span>
							<span className="text-xs leading-relaxed text-muted">{hint}</span>
						</a>
					</li>
				))}
			</ul>

			<div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-[10px] border border-hairline bg-paper px-3 py-2.5">
				<div className="flex items-center gap-3">
					<span className="text-[13px] font-semibold">Appearance</span>
					<ThemeToggle />
				</div>
				<Button variant="ghost" size="sm" className="ml-auto" onClick={() => void session.logout()}>
					<LogOut className="size-3.5" aria-hidden />
					Log out
				</Button>
			</div>
		</Page>
	);
}
